import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap, TweenLite} from "gsap";
import {Mental} from "../Mental";
import {IBetWayWinData} from "../server/MentalData";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {MentalHelper} from "../utils/MentalHelper";

export class MentalWinWaysView extends PIXI.Container implements StageLayer {
    private _totalWinWays: PIXI.BitmapText; // this is the number of win ways shown after initial win count up(placed below the MAIN count up shown on screen's centre).
    private _totalWinWaysText: PIXI.Sprite; // this is the win ways text shown after initial win count up(placed below the MAIN count up shown on screen's centre).
    private _totalWinWaysContainer: PIXI.Container;
    private _toggleWinAmount: PIXI.BitmapText; // this is the win amount shown during toggling.
    private _toggleWinWaysContainer: PIXI.Container;
    private _toggleWinWays: PIXI.BitmapText; // this is the number of win ways shown during toggling.
    private _toggleWinWaysText: PIXI.Sprite; // this is the win ways text shown during toggling.
    private _fadeInToggleTween: TweenLite;

    // #365
    private _totalWinWaysHideTween: TweenLite;

    constructor() {
        super();

        this.init();
    }

    // #417
    public showInitialWinWays(): gsap.core.Timeline {
        if (!Mental.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame) {
            return gsap.timeline();
        }

        const tl: gsap.core.Timeline = gsap.timeline(),
            betWays: IBetWayWinData[] = Mental.model.gameData.betWayWins,
            container: PIXI.Container = new PIXI.Container();

        container.position.set(360);
        container.alpha = 0;

        betWays.forEach((betWay: IBetWayWinData, idx: number) => {
            const winAmount: PIXI.BitmapText = new PIXI.BitmapText(betWay.multipliedAmount.toFixed(2), {
                    fontName: "MentalNumbersWinCountUp",
                    fontSize: 12
                }),
                waysContainer: PIXI.Container = new PIXI.Container(),
                waysAmount: PIXI.BitmapText = new PIXI.BitmapText(betWay.numberOfCombinations + "", {
                    fontName: "xNudgeWinCountUp",
                    fontSize: 18
                }),
                waysText: PIXI.Sprite = new PIXI.Sprite((betWay.numberOfCombinations === 1) ? Mental.assetLoader.getTextures("way")[0] : Mental.assetLoader.getTextures("ways")[0]);

            waysText.x = ((betWay.numberOfCombinations === 1) ? waysAmount.width - 15 : waysAmount.width);
            waysText.scale.set(0.8);

            winAmount.y = (idx * 30) + (idx * 80);
            winAmount.pivot.set(winAmount.width / 2, winAmount.height / 2);

            waysText.y = -10;
            waysContainer.y = (winAmount.y + 20);

            waysContainer.addChild(waysText, waysAmount);
            waysContainer.pivot.set(waysContainer.width / 2 - 10, 0);

            container.addChild(waysContainer, winAmount);
        });

        if (betWays.length > 5) {
            container.scale.set(0.65);
        }
        container.y -= (container.height / 2);

        this.addChild(container);

        tl.to(container, {
            duration: 0.1,
            alpha: 1
        });
        tl.to(container, {
            duration: 0.3,
            y: "-=50"
        });
        tl.to(container, {
            duration: 0.1,
            alpha: 0
        });
        tl.add(() => {
            container.removeChildren();
            this.removeChild(container);
        });

        return tl;
    }

    public showTotalWinWaysItems(isBigWin: boolean): void {
        let totalWaysWon: number = 0;
        const betWaysData: IBetWayWinData[] = Mental.model.gameData.betWayWins;
        for (let i: number = 0; i < betWaysData.length; i++) {
            totalWaysWon += betWaysData[i].numberOfCombinations;
        }

        this._totalWinWays.text = totalWaysWon + "";

        if (this._totalWinWays.text === "1") {
            this._totalWinWaysText.texture = Mental.assetLoader.getTextures("way")[0];
            this._totalWinWaysText.x = (this._totalWinWays.width - 13);
        } else {
            this._totalWinWaysText.texture = Mental.assetLoader.getTextures("ways")[0];
            this._totalWinWaysText.x = (this._totalWinWays.width);
        }

        this._totalWinWaysContainer.pivot.set(this._totalWinWaysContainer.width / 2, this._totalWinWaysContainer.height / 2);
        if (isBigWin) {
            if (NolimitApplication.isLandscape) {
                this._totalWinWaysContainer.y = 520;
            } else {
                this._totalWinWaysContainer.y = 660;
            }
        } else {
            this._totalWinWaysContainer.y = 440;
        }
        this._totalWinWaysContainer.visible = true;
        this._totalWinWaysContainer.alpha = 1;
    }

    public hideTotalWinWaysItems(duration: number): void {
        this._totalWinWaysHideTween = TweenLite.to(this._totalWinWaysContainer, {
            alpha: 0,
            duration: duration,
            onComplete: () => {
                this._totalWinWaysContainer.visible = false;
            }
        });
    }

    // #365
    public onAbort(): void {
        if (this._totalWinWaysHideTween) {
            this._totalWinWaysHideTween.pause();
            this._totalWinWaysHideTween.kill();
        }
    }

    public showToggleWinWaysItems(winAmount: number, ways: number): void {
        if (this._totalWinWaysContainer.visible) {
            this._totalWinWaysContainer.visible = false;
        }

        Mental.bigWinPresentation.hideCountUp();

        this._toggleWinAmount.text = winAmount.toFixed(2);
        this._toggleWinAmount.pivot.set(this._toggleWinAmount.width / 2, this._toggleWinAmount.height / 2);

        this._toggleWinWays.text = ways + "";

        if (this._toggleWinWays.text === "1") {
            this._toggleWinWaysText.texture = Mental.assetLoader.getTextures("way")[0];
            this._toggleWinWaysText.x = (this._toggleWinWays.width - 15);
        } else {
            this._toggleWinWaysText.texture = Mental.assetLoader.getTextures("ways")[0];
            this._toggleWinWaysText.x = (this._toggleWinWays.width);
        }

        this._toggleWinWaysContainer.pivot.set(this._toggleWinWaysContainer.width / 2, this._toggleWinWaysContainer.height / 2);
        if (!this._toggleWinWaysContainer.visible) {
            this._toggleWinAmount.visible = true;
            this._toggleWinWaysContainer.visible = true;
            this._fadeInToggleTween = TweenLite.to([this._toggleWinAmount, this._toggleWinWaysContainer], {
                alpha: 1,
                duration: 0.3
            });
        }
    }

    public fadeOutToggleWinWaysItems(): void {
        TweenLite.to([this._toggleWinAmount, this._toggleWinWaysContainer], {
            alpha: 0,
            duration: 0.6,
            onComplete: () => this.removeToggleWinWaysItems()
        });
    }

    public removeToggleWinWaysItems(): void {
        if (this._fadeInToggleTween) {
            this._fadeInToggleTween.kill();
        }
        this._toggleWinAmount.visible = false;
        this._toggleWinWaysContainer.visible = false;
    }

    private init(): void {
        this.createTotalWinWaysItems();
        this.createToggleWinWaysItems();
    }

    private createTotalWinWaysItems(): void {
        this._totalWinWaysContainer = new PIXI.Container();
        this._totalWinWaysContainer.visible = false;
        this._totalWinWaysContainer.pivot.set(this._totalWinWaysContainer.width / 2, this._totalWinWaysContainer.height / 2);
        this._totalWinWaysContainer.position.set(360, 440);

        this._totalWinWaysText = new PIXI.Sprite(Mental.assetLoader.getTextures("ways")[0]);
        this._totalWinWaysText.position.y = -5;

        this._totalWinWays = new PIXI.BitmapText("", {
            fontName: "xNudgeWinCountUp",
            fontSize: 30
        });

        this._totalWinWaysContainer.addChild(this._totalWinWaysText, this._totalWinWays);
        this.addChild(this._totalWinWaysContainer);
    }

    private createToggleWinWaysItems(): void {
        this._toggleWinWaysContainer = new PIXI.Container();
        this._toggleWinWaysContainer.visible = false;
        this._toggleWinWaysContainer.pivot.set(this._toggleWinWaysContainer.width / 2, this._toggleWinWaysContainer.height / 2);
        this._toggleWinWaysContainer.position.set(360, 400);

        this._toggleWinWaysText = new PIXI.Sprite(Mental.assetLoader.getTextures("ways")[0]);
        this._toggleWinWaysText.position.y = -10;

        this._toggleWinWays = new PIXI.BitmapText("", {
            fontName: "xNudgeWinCountUp",
            fontSize: 25
        });

        this._toggleWinWaysContainer.addChild(this._toggleWinWaysText, this._toggleWinWays);

        this._toggleWinAmount = new PIXI.BitmapText("", {
            fontName: "MentalNumbersWinCountUp",
            fontSize: 18
        });
        this._toggleWinAmount.visible = false;
        this._toggleWinAmount.position.set(360, 320);

        this.addChild(this._toggleWinWaysContainer, this._toggleWinAmount);
    }
}